import { Menu, X } from "lucide-react";
import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";

import { LocaleSwitcher } from "@/components/core/locale-switcher";
import { Button } from "@/components/ui/button";
import { buttonVariants } from "@/components/ui/button";
import { useLocale } from "@/i18n/locale-context";
import { cn } from "@/lib/utils";
import type { NavigationItem } from "@/types/site";

interface NavbarProps {
  items: NavigationItem[];
  siteName: string;
}

const navLinkClassName = ({ isActive }: { isActive: boolean }): string =>
  cn(
    "group relative text-sm uppercase tracking-[0.2em] transition-[color,opacity] duration-300 after:absolute after:bottom-[-0.45rem] after:left-0 after:h-px after:w-full after:origin-left after:scale-x-0 after:bg-current after:transition-transform after:duration-300 after:content-[''] hover:text-foreground hover:after:scale-x-100",
    isActive ? "text-foreground after:scale-x-100" : "text-foreground/62",
  );

export function Navbar({ items, siteName }: NavbarProps): JSX.Element {
  const { localePath, t } = useLocale();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const adminHref = localePath("/admin");
  const consultationHref = localePath("/inquiry");

  useEffect(() => {
    if (typeof document === "undefined") return;
    document.body.dataset.navOpen = String(isOpen);

    return () => {
      if (typeof document === "undefined") return;
      delete document.body.dataset.navOpen;
    };
  }, [isOpen]);

  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname, location.search, location.hash]);

  return (
    <header className="sticky top-0 z-50 border-b border-border/70 bg-white">
      <div className="mx-auto flex max-w-7xl items-center justify-between gap-3 px-4 py-4 sm:px-6 lg:px-10">
        <Link
          className="group flex max-w-[14rem] items-center gap-2 text-[0.78rem] font-semibold uppercase leading-tight tracking-[0.22em] text-foreground transition-opacity duration-300 hover:opacity-80 sm:max-w-none sm:gap-3 sm:text-sm sm:tracking-[0.3em]"
          to={localePath("/")}
        >
          <img
            alt=""
            aria-hidden="true"
            className="size-5 shrink-0 transition-transform duration-500 ease-out motion-safe:group-hover:rotate-[12deg] motion-safe:group-hover:scale-110 sm:size-6"
            data-testid="navbar-brand-icon"
            decoding="async"
            height="24"
            loading="eager"
            src="/Icon/icon.svg"
            width="24"
          />
          {siteName}
        </Link>
        <nav
          aria-label={t("common.primaryNavigation")}
          className="hidden items-center gap-7 md:flex"
        >
          {items.map((item) => (
            <NavLink
              end={item.href === "/"}
              key={item.href}
              className={navLinkClassName}
              to={localePath(item.href)}
            >
              {item.label}
            </NavLink>
          ))}
          <LocaleSwitcher />
          <Link
            className={buttonVariants({ size: "compact", variant: "outline" })}
            to={adminHref}
          >
            {t("admin.loginButton")}
          </Link>
          <Link
            className={buttonVariants({ size: "compact" })}
            to={consultationHref}
          >
            {t("actions.consultation")}
          </Link>
        </nav>
        <Button
          aria-controls="mobile-navigation"
          aria-expanded={isOpen}
          aria-label={isOpen ? t("actions.closeMenu") : t("actions.openMenu")}
          className="shrink-0 md:hidden"
          onClick={() => {
            setIsOpen((currentState) => !currentState);
          }}
          size="compact"
          variant="outline"
        >
          {isOpen ? <X aria-hidden="true" /> : <Menu aria-hidden="true" />}
        </Button>
      </div>
      <div
        className={cn(
          "overflow-hidden border-t border-border/70 bg-white px-4 transition-[max-height,opacity] duration-300 sm:px-6 md:hidden",
          isOpen
            ? "max-h-[calc(100svh-5rem)] opacity-100"
            : "max-h-0 opacity-0",
        )}
        id="mobile-navigation"
      >
        <nav
          aria-label={t("common.mobileNavigation")}
          className="flex max-h-[calc(100svh-5rem)] flex-col gap-2 overflow-y-auto py-4"
        >
          {items.map((item) => (
            <NavLink
              end={item.href === "/"}
              key={item.href}
              className={({ isActive }) =>
                cn(
                  "border border-transparent px-3 py-3 text-sm uppercase tracking-[0.18em] transition-[transform,border-color,background-color,color] duration-300 motion-safe:hover:translate-x-1",
                  isActive
                    ? "bg-primary text-primary-foreground"
                    : "text-foreground/72 hover:border-border hover:text-foreground",
                )
              }
              onClick={() => {
                setIsOpen(false);
              }}
              to={localePath(item.href)}
            >
              {item.label}
            </NavLink>
          ))}
          <div className="pt-2 pb-2">
            <LocaleSwitcher />
          </div>
          <Link
            className={buttonVariants({
              className: "w-full",
              size: "compact",
              variant: "outline",
            })}
            onClick={() => {
              setIsOpen(false);
            }}
            to={adminHref}
          >
            {t("admin.loginButton")}
          </Link>
          <Link
            className={buttonVariants({ className: "w-full", size: "compact" })}
            onClick={() => {
              setIsOpen(false);
            }}
            to={consultationHref}
          >
            {t("actions.consultation")}
          </Link>
        </nav>
      </div>
    </header>
  );
}
